import { Button } from '@/components/Button/Button';
import { CsvImportValidationResult } from '@/screens/ReportCreation/components/CsvImport/CsvImportValidationResult';
import { useCsvFilePicker } from '@screens/ReportCreation/components/CsvImport/hooks/useCsvFilePicker';
import { useCsvImport } from '@screens/ReportCreation/components/CsvImport/hooks/useCsvImport';
import { useCsvTemplateDownload } from '@/screens/ReportCreation/components/CsvImport/hooks/useCsvTemplateDownload';
import { FileUploaderField } from '@/screens/ReportCreation/components/fields/FileUploaderField/FileUploaderField';
import { Ionicons } from '@expo/vector-icons';
import { useAppTranslation } from '@hooks/useAppTranslation';
import React, { useEffect } from 'react';
import { Modal, Pressable } from 'react-native';
import { SizableText, useTheme, XStack, YStack } from 'tamagui';
import { useReferentialsData } from '@hooks/useReferentialsData';

interface AddReportCsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
}

export const AddReportCsvImportModal = ({
  isOpen,
  onClose,
  onBack,
}: AddReportCsvImportModalProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const { downloadTemplate } = useCsvTemplateDownload();
  const { files, feedbackMessage, canImport, onUploadPress, onRemoveFile } =
    useCsvFilePicker();
  const { data: referentials } = useReferentialsData();
  const {
    isValidating,
    validationResult,
    fileValidationErrorMessage,
    validateCsvFile,
    resetValidation,
    publishCsv,
    sendCsvAsDraft,
    isImporting,
  } = useCsvImport(referentials);

  useEffect(() => {
    resetValidation();
  }, [files, resetValidation]);

  const hasValidationResult = Boolean(validationResult);
  const hasValidationErrors = (validationResult?.errors.length ?? 0) > 0;

  const validateCsv = (): Promise<void> => validateCsvFile(files[0]);

  const handleDownloadTemplatePress = (): void => {
    downloadTemplate();
  };

  return (
    <Modal
      animationType="fade"
      transparent
      visible={isOpen}
      onRequestClose={onClose}
    >
      <YStack
        flex={1}
        backgroundColor="$shadow"
        alignItems="center"
        justifyContent="center"
        padding="$true"
      >
        <Pressable
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
          onPress={onClose}
        />

        <YStack
          width="100%"
          maxWidth={720}
          backgroundColor="$background"
          borderRadius="$true"
          padding="$true"
          gap="$true"
          zIndex="$1"
        >
          <XStack alignItems="center" justifyContent="space-between">
            <SizableText size="$h6" fontFamily="$bold">
              {t('reportCreation.csvImportModal.title')}
            </SizableText>
            <Button
              variant="ghost"
              icon={<Ionicons name="close" size={24} color={theme.color.val} />}
              onPress={onClose}
              maxWidth={30}
              padding={0}
            />
          </XStack>

          <FileUploaderField
            files={files}
            multiple={false}
            onUploadPress={() => {
              void onUploadPress();
            }}
            onRemoveFile={onRemoveFile}
            uploadPrompt={t('reportCreation.csvImportModal.dropzoneTitle')}
            helperText={t('reportCreation.csvImportModal.dropzoneSubtitle')}
            errorMessage={feedbackMessage}
          />

          {fileValidationErrorMessage && (
            <SizableText color="$error" size="$2">
              {fileValidationErrorMessage}
            </SizableText>
          )}

          {validationResult && (
            <CsvImportValidationResult result={validationResult} />
          )}

          <XStack justifyContent="space-between" alignItems="center">
            <Button
              variant="outlined"
              title={t('reportCreation.csvImportModal.downloadTemplate')}
              icon={
                <Ionicons
                  name="download-outline"
                  size={18}
                  color={theme.color.val}
                />
              }
              onPress={handleDownloadTemplatePress}
            />

            <XStack gap="$true">
              <Button
                variant="outlined"
                title={t('reportCreation.csvImportModal.back')}
                onPress={onBack}
                minWidth={160}
              />
              {!hasValidationResult && (
                <Button
                  title={t('reportCreation.csvImportModal.import')}
                  onPress={validateCsv}
                  minWidth={160}
                  disabled={!canImport || isImporting}
                  loading={isValidating || isImporting}
                />
              )}
              {hasValidationResult && (
                <Button
                  title={t('reportCreation.csvImportModal.saveAsDraft')}
                  onPress={sendCsvAsDraft}
                  minWidth={160}
                  disabled={!canImport || isImporting}
                  loading={isValidating || isImporting}
                />
              )}
              {hasValidationResult && !hasValidationErrors && (
                <Button
                  title={t('reportCreation.csvImportModal.publish')}
                  onPress={publishCsv}
                  minWidth={160}
                  disabled={!canImport || isImporting || hasValidationErrors}
                  loading={isValidating || isImporting}
                />
              )}
            </XStack>
          </XStack>
        </YStack>
      </YStack>
    </Modal>
  );
};
